/** @format */

import cn from 'classnames';
import { useAddress } from 'core-lib/hooks/useAddress';
import { formatTitleCase } from 'core-lib/utils/common';
import { customToast } from 'core-lib/utils/customToast';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'ui-v2/components/Button';
import Radio from 'ui-v2/components/Radio/Radio';
import { Modal, ModalTypeTypes } from 'ui-v2/components/Modal/Modal';
import { Typography } from 'ui-v2/components/Typography';
import { checkServiceability } from '../../services/checkServiceability';
import { updateUserAddress } from '../../services/updateUserAddress';
import { UserAddress } from '../../types/types';
import { checkValidAddress } from '../../utils/utils';
import { ConfirmDelete } from '../ConfirmDelete/ConfirmDelete';
import { Unserviceable } from '../Unserviceable/Unserviceable';
import UpdateDetails from '../UpdateDetails';
import s from './SelectAddress.module.scss';
import StatusBar from 'core-lib/components/StatusBar';
import NoAddressSVG from '../../icons/noAddress.svg';
import Icon from 'ui-v2/components/Icon/Icon';

type SelectAddressProps = {
  isShow: boolean;
  onClose: () => void;
  variant: ModalTypeTypes;
  userSelectedAddress: UserAddress;
  onUseAddress: (arg: any) => void;
  onNewAddress: () => void;
  checkServiceable: boolean;
  serviceName: string;
  onEditAddress: (address) => void;
  zIndex: number;
  withProceedBtn?: boolean;
  blurredBg?: boolean;
};

export const SelectAddress = ({
  isShow,
  onClose,
  variant,
  userSelectedAddress,
  onUseAddress,
  onNewAddress,
  checkServiceable,
  serviceName,
  onEditAddress,
  zIndex,
  withProceedBtn = false,
  blurredBg = false,
}: SelectAddressProps) => {
  const [address, setAddress] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showUnserviceable, setShowUnserviceable] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showUpdateDetails, setShowUpdateDetails] = useState(false);
  const [showMoreItem, setShowMoreItem] = useState(null);
  const { addressList: userAddresses } = useSelector(state => state.auth.userAddresses);

  // fetch all addresses
  const { fetchUserAddresses } = useAddress();

  const onDeleteAddress = async address => {
    setIsLoading(true);
    const payload = {
      id: address?.id,
      city: address?.city,
      latitude: address?.latitude || '',
      longitude: address?.longitude || '',
      locality: address?.locality,
      address: address?.address || 'NA',
      active: false,
      pincode: address?.pincode,
      state: address?.state?.trim(),
      landmark: address?.landmark?.trim(),
      address_type: address?.addressType,
      secondary_address_type: address?.addressTag,
      user_address_meta: {
        name: address?.metaData?.name,
        phone_number: address?.metaData?.phone,
        email_id: address?.metaData?.email,
      },
    };

    const { response } = await updateUserAddress(payload);
    if (response) {
      fetchUserAddresses();
      customToast({
        variant: 'positivePriority',
        label: 'Address deleted successfully',
      });
    }
    setShowConfirmDelete(false);
    setShowMoreItem(null);
    setIsLoading(false);
  };

  const handleMoreItem = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (index === showMoreItem) {
      setShowMoreItem(null);
    } else {
      setShowMoreItem(index);
    }
  };

  const formatLabel = e => {
    const name = e?.metaData?.name ? `${formatTitleCase(e.metaData.name)},` : '';
    const address = e?.address ? `${e?.address},` : '';
    const locality = e?.locality ? `${e?.locality},` : '';
    const landmark = e?.landmark ? `${e?.landmark},` : '';
    const city = formatTitleCase(e?.city || '');

    return `${name} ${address} ${locality} ${landmark} ${city}.`;
  };

  const onSelectAddress = async selectedAddress => {
    if (variant !== 'page') {
      if (checkValidAddress(selectedAddress)) {
        if (checkServiceable) {
          const { isServiceable } = await checkServiceability({
            currentAddress: selectedAddress,
            serviceName,
          });
          if (isServiceable) {
            onUseAddress(selectedAddress);
          } else {
            setShowUnserviceable(true);
          }
        } else {
          onUseAddress(selectedAddress);
        }
      } else {
        setShowUpdateDetails(true);
        setSelectedAddress(selectedAddress);
      }
    }
  };

  useEffect(() => {
    if (userSelectedAddress) {
      setAddress(userSelectedAddress);
    } else {
      setAddress(userAddresses[0]);
    }
    setSelectedAddress(null);
  }, [userAddresses, userSelectedAddress]);

  return (
    <Modal
      show={isShow}
      head={{
        show: true,
        title: variant === 'page' ? 'My address book' : 'Select address',
        onClose: onClose,
      }}
      closeOnClickOutside={false}
      noPadding
      blurredBg={blurredBg}
      variant={variant}
      animation={{ type: variant === 'page' ? 'rtl' : 'btt', timeout: 's' }}
      rootStyle={{ zIndex }}
    >
      <StatusBar statusBarColor="#FFFFFF" theme="dark" cleanUpOnUnmount />
      {userAddresses?.length ? (
        <div className={s.root}>
          <div className={s.addNewAddress} onClick={() => onNewAddress()}>
            <Icon name="PlusCircleRegular" width={20} height={20} color="brand" />
            <Typography label="Add a new address" variant="label_2_medium" color="brand" />
          </div>

          <div className={s.container}>
            <Typography
              label="Saved addresses"
              variant="label_3_semi_bold"
              style={{ marginBottom: '16px' }}
            />
            {userAddresses?.map((e, index) => (
              <div
                className={cn(s.wraper, {
                  [s.margin_bottom]: index === userAddresses.length - 1 && withProceedBtn,
                })}
                key={e?.id}
                onClick={() => {
                  if (withProceedBtn) setAddress(e);
                  else onSelectAddress(e);
                }}
              >
                <div className={s.addressCard}>
                  <div className={s.wrapAddress}>
                    <div className={s.radioBtn}>
                      {variant === 'page' ? (
                        e.addressType === 2 ? (
                          <Icon name="HomeBold" width={24} height={24} />
                        ) : e.addressType === 1 ? (
                          <Icon name="BuildingRegular" width={24} height={24} />
                        ) : (
                          <Icon name="LocationRegular" width={24} height={24} />
                        )
                      ) : (
                        <Radio
                          isChecked={e?.id === address?.id}
                          onChange={event => {
                            event.stopPropagation();
                            onSelectAddress(e);
                          }}
                        />
                      )}
                    </div>
                    <div className={s.address}>
                      <div className={s.title}>
                        {e?.addressTag && (
                          <Typography
                            label={formatTitleCase(e.addressTag || 'Address')}
                            variant="label_2_semi_bold"
                          />
                        )}
                      </div>

                      <div className={s.subTitle}>
                        <Typography
                          label={formatLabel(e)}
                          variant="label_2_regular"
                          color="secondary"
                        />

                        <Typography
                          label={`${formatTitleCase(e?.state)} ${e?.pincode}`}
                          variant="label_2_regular"
                          color="secondary"
                        />

                        {e?.metaData?.phone && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <Typography
                              label={`Ph.No:`}
                              variant="label_3_regular"
                              style={{ marginTop: '8px' }}
                            />
                            <Typography
                              label={`${e?.metaData?.phone}`}
                              variant="label_2_semi_bold"
                              style={{ marginTop: '8px' }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={s.edit}>
                      <div
                        onClick={event => {
                          event.stopPropagation();
                          onEditAddress(e);
                        }}
                      >
                        <Icon name="Edit2Regular" height={20} width={20} />
                      </div>
                      <div
                        onClick={event => {
                          event.stopPropagation();
                          setShowConfirmDelete(true);
                          handleMoreItem(event, index);
                        }}
                      >
                        <Icon name="TrashRegular" height={20} width={20} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {withProceedBtn && (
              <div className={s.proceed}>
                <Button
                  size="medium"
                  variant="primary"
                  label="Proceed"
                  fullWidth
                  onClick={() => onSelectAddress(address)}
                />
              </div>
            )}
          </div>

          <Unserviceable
            isShow={showUnserviceable}
            onClose={() => setShowUnserviceable(false)}
            onDone={() => setShowUnserviceable(false)}
            zIndex={zIndex}
          />

          <ConfirmDelete
            isShow={showConfirmDelete}
            onClose={() => {
              setShowConfirmDelete(false);
              setShowMoreItem(null);
            }}
            isLoading={isLoading}
            onDone={() => {
              onDeleteAddress(userAddresses[showMoreItem]);
            }}
            zIndex={zIndex}
          />

          <UpdateDetails
            isShow={showUpdateDetails}
            onClose={() => setShowUpdateDetails(false)}
            onDone={() => onEditAddress(selectedAddress)}
            zIndex={zIndex}
          />
        </div>
      ) : (
        <div className={s.noAddress}>
          <div className={s.upper}>
            <div className={s.image_container}>
              <NoAddressSVG />
            </div>
            <div className={s.text_container}>
              <div className={s.title}>
                <Typography variant="h_4_medium" label="Oops! No saved address" />
              </div>
              <div className={s.sub_title}>
                <Typography
                  variant="body_2_regular"
                  color="secondary"
                  label={`You don’t have any saved address. Saving address
                      helps checkout faster`}
                />
              </div>
            </div>
          </div>

          <div className={s.button_container}>
            <Button
              variant="primary"
              size="medium"
              label="Add a new address"
              onClick={() => onNewAddress()}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};
